import { toNumber } from 'lodash';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleDialog } from 'src/components/modal';
import { ScreenConfigModal } from 'src/components/modal/screenConfigModal/ScreenConfigModal';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { AssemblyJourneys, CodJourney } from 'src/constants/assemblyJourneys';
import { PATHS } from 'src/constants/paths';
import { LayoutEditSidebar } from 'src/features/assemblies/layouts/editLayoutsPage/modules/layoutEditSidebar/LayoutEditSidebar';
import { useModal } from 'src/hooks';
import { useCreateTakeoverSection } from 'src/hooks/api/layouts/useCreateTakeoverSection';
import { useDeleteTakeoverSection } from 'src/hooks/api/layouts/useDeleteTakeoverSection';
import { useUpdateLayout } from 'src/hooks/api/layouts/useUpdateLayout';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { IStatus } from 'src/interfaces';
import { lang, parseLayoutConfig } from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import {
  LayoutEditContainer,
  SidebarContainer,
} from './EditLayoutsPage.styled';
import {
  isSectionCreationPath,
  persistLayoutDraft,
} from './editLayoutsPage.utils';
import { useEditLayoutsPage } from './hooks/useEditLayoutsPage.hook';
import { LayoutEditContentContainer } from './modules/LayoutEditContentContainer/LayoutEditContentContainer';

export const EditLayoutsPage = () => {
  const { layoutId = '' } = useParams();
  const navigate = useNavigate();
  const discardModal = useModal();

  const {
    layouts,
    canSaveAsDraft,
    screenNames,
    sidebarScreenOrder,
    isLayoutScreensLoading,
    isLayoutScreensFetching,
    formMethods,
    screensWithTakeover,
    globalComponentsScreen,
  } = useEditLayoutsPage(toNumber(layoutId));

  const shouldBlockNavigation = (nextPath: string | undefined) => {
    // The section-creation page is a sub-step of the same layout workflow, not
    // a genuine exit — never show the "leave" modal for it. Persist any unsaved
    // changes first so they are restored when the user returns to this page.
    if (isSectionCreationPath(nextPath)) {
      if (formMethods.formState.isDirty) {
        persistLayoutDraft(layoutId, formMethods);
      }

      return false;
    }

    return formMethods.formState.isDirty;
  };

  useNavigateBlocker(shouldBlockNavigation);
  const [isDisabled, setIsDisabled] = useState(false);

  const updateLayout = useUpdateLayout('name');
  const createTakeoverSection = useCreateTakeoverSection();
  const deleteTakeoverSection = useDeleteTakeoverSection();
  const screenConfigModal = useModal();
  const [configScreenIndex, setConfigScreenIndex] = useState(0);
  const [isSaveAsReadyLoading, setIsSaveAsReadyLoading] = useState(false);
  const [activeAssemblyJourney, setActiveAssemblyJourney] = useState<
    AssemblyJourneys | undefined
  >(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);

  const [defaultLayout] = layouts;
  const sectionTypes = defaultLayout?.config?.sectionTypes;
  const { hasSectionRotation, hasTakeOver, hasDYR } =
    checkSectionTypes(sectionTypes);

  const isDefaultLayoutScreensFilled = defaultLayout?.screens.every(
    (screen) => !screen.name.includes('placeholder'),
  );

  const isLayoutReadyToSave = () => {
    if (!isDefaultLayoutScreensFilled) return false;

    if (hasSectionRotation) {
      const hasScreenRotationEvent = defaultLayout?.screens?.some((screen) =>
        screen.sections.some(
          (section) => section.config?.events?.SECTION_TIMER !== undefined,
        ),
      );
      if (!hasScreenRotationEvent) return false;
    }

    if (hasTakeOver) {
      const selectedTakeOverScreens = defaultLayout?.screens?.map(
        (_, idx) => formMethods.watch(`screen_${idx}`) ?? false,
      );
      const noTakeoverSelected = selectedTakeOverScreens.every(
        (value) => value === false,
      );

      if (noTakeoverSelected) return false;
    }

    return true;
  };

  const handleOpenConfig = (index: number) => {
    setConfigScreenIndex(index);

    const layoutConfig = parseLayoutConfig(
      layouts?.[0]?.config?.gridLines?.[index] || '',
    );
    formMethods.setValue('orientation', layoutConfig.orientation);
    formMethods.setValue('disclaimer', !!layoutConfig.disclaimerPosition);
    formMethods.setValue(
      'screen_grid',
      `${layoutConfig.cols} X ${layoutConfig.rows}`,
    );
    formMethods.setValue('size', layoutConfig.disclaimerType);
    formMethods.setValue('placement', layoutConfig.disclaimerPosition);
    formMethods.clearErrors();
    screenConfigModal.onOpenModal();
    setActiveAssemblyJourney(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
  };

  const handleSave = async (status: IStatus) => {
    const data = formMethods.getValues();
    if (status === IStatus.READY) {
      setIsSaveAsReadyLoading(true);
    }

    const { screensToAddTakeover, screensToRemoveTakeover } =
      layouts?.[0]?.screens.reduce(
        (acc, _, screenIndex) => {
          const hasTakeover = formMethods.watch(`screen_${screenIndex}`);
          const isInScreensWithTakeover =
            screensWithTakeover.includes(screenIndex);

          if (hasTakeover && !isInScreensWithTakeover) {
            acc.screensToAddTakeover.push(screenIndex);
          } else if (!hasTakeover && isInScreensWithTakeover) {
            acc.screensToRemoveTakeover.push(screenIndex);
          }

          return acc;
        },
        {
          screensToAddTakeover: [] as number[],
          screensToRemoveTakeover: [] as number[],
        },
      ) || { screensToAddTakeover: [], screensToRemoveTakeover: [] };

    await Promise.all([
      ...screensToAddTakeover.map((screenIndex) =>
        createTakeoverSection.mutateAsync({
          screen: layouts?.[0]?.screens[screenIndex],
        }),
      ),
      ...screensToRemoveTakeover.map((screenIndex) =>
        deleteTakeoverSection.mutateAsync({
          screen: layouts?.[0]?.screens[screenIndex],
        }),
      ),
    ]);

    await updateLayout.mutateAsync({
      id: layoutId,
      index: 1,
      name: data['name'],
      ...(status === IStatus.READY && { status }),
    });

    flushSync(() => {
      formMethods.reset(undefined, { keepValues: true });
    });

    const successMessage = status
      ? lang.editLayoutsSidebar.saveAsReadyMessage(data['name'])
      : lang.editLayoutsSidebar.saveAsDraftMessage(data['name']);
    Snackbar.success(successMessage);

    // Navigate back if saving as ready
    if (status === IStatus.READY) {
      navigate({
        pathname: PATHS.ASSEMBLY_LAYOUTS,
      });
    }
  };

  if (isLayoutScreensLoading || isLayoutScreensFetching) {
    return <Progress />;
  }

  return (
    <FormProvider {...formMethods}>
      <LayoutEditContainer>
        <SidebarContainer>
          <LayoutEditSidebar
            screens={layouts?.[0]?.screens ?? []}
            formMethods={formMethods}
            layoutId={layoutId}
            sectionTypes={sectionTypes}
            activeAssemblyJourney={activeAssemblyJourney}
            setActiveAssemblyJourney={setActiveAssemblyJourney}
            screenNames={screenNames}
            screenDisplayOrder={sidebarScreenOrder}
            globalComponentsScreen={globalComponentsScreen}
          />
        </SidebarContainer>

        <LayoutEditContentContainer
          layouts={layouts}
          formMethods={formMethods}
          canSaveAsDraft={canSaveAsDraft}
          isSaveAsReadyLoading={isSaveAsReadyLoading}
          hasDYR={hasDYR}
          screenNames={screenNames}
          isLayoutReadyToSave={isLayoutReadyToSave}
          activeAssemblyJourney={activeAssemblyJourney}
          handleOpenConfig={handleOpenConfig}
          handleSave={handleSave}
          updateLayout={updateLayout}
          sectionTypes={sectionTypes}
          discardModal={discardModal}
          globalComponentsScreen={globalComponentsScreen}
        />
      </LayoutEditContainer>
      <ScreenConfigModal
        screenConfigModal={screenConfigModal}
        layout={layouts?.[0]}
        formMethods={formMethods}
        configScreenIndex={configScreenIndex}
        layoutId={layoutId}
        screenName={screenNames?.[configScreenIndex]}
      />
      <SimpleDialog
        open={discardModal.open}
        onClose={discardModal.onCloseModal}
        onClickCancel={discardModal.onCloseModal}
        onClickConfirm={() => {
          setIsDisabled(true);
          handleSave(IStatus.READY);
        }}
        title={lang.publishLayoutModal.title}
        subTitle={lang.publishLayoutModal.subTitle}
        confirmText={lang.confirm}
        cancelText={lang.cancel}
        disabled={isDisabled}
      />
    </FormProvider>
  );
};
